import React, { memo } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningOutlinedIcon from '@mui/icons-material/WarningOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import reduce from 'lodash/reduce';
import EmojiEmotionsOutlinedIcon from '@mui/icons-material/EmojiEmotionsOutlined';
import PropTypes from 'prop-types';
import makeStyles from '@mui/styles/makeStyles';
import { Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import 'react-toastify/dist/ReactToastify.css';
import('react-toastify/dist/ReactToastify.min.css');

// import { isProd } from '../../config';
// // for react-toastify to get style
// if (!isProd) {
//   try {
//     import('react-toastify/dist/ReactToastify.min.css');
//   } catch (err) {
//     console.log('err', err);
//   } finally {
//     console.log('finally');
//   }
// }

// This is the content of the generic toast!
const toastContentStyles = makeStyles(() => ({
    iconContainer: {
        display: 'flex',
        alignItems: 'center',
        fontSize: '28px'
    },
    headerContainer: {
        fontWeight: 'bold',
        marginLeft: '8px'
    },
    bodyContainer: {
        display: 'flex',
        justifyContent: 'center',
        marginBottom: '8px'
    },
    closeButton: {
        position: 'absolute',
        top: '8px',
        right: '8px'
    }
}));

const Content = (props) => {
    const { headerText, bodyText, Icon } = props;
    const classes = toastContentStyles();
    return (
        <Grid container direction="column">
            <Grid item>
                <Grid container alignItems="center">
                    <Icon className={`icon ${classes.iconContainer}`} />
                    <Typography variant="subtitle1" className={classes.headerContainer}>
                        {headerText}
                    </Typography>
                </Grid>
            </Grid>
            {bodyText && (
                <Grid item className={classes.bodyContainer}>
                    <Typography variant="body1" align="center">
                        {bodyText}
                    </Typography>
                </Grid>
            )}
            <IconButton className={classes.closeButton} onClick={toast.dismiss} size="large">
                <CloseIcon />
            </IconButton>
        </Grid>
    );
};

const ToastContent = memo(Content);
Content.propTypes = {
    headerText: PropTypes.string.isRequired,
    bodyText: PropTypes.string,
    Icon: PropTypes.elementType.isRequired
};

// Here you can add another generic toast!
const toastTypes = {
    success: {
        defaultHeaderText: 'Success!',
        Icon: CheckCircleOutlineIcon,
        getContainerJss: (theme) => ({
            backgroundColor: theme.palette.success.lighten90,
            color: theme.palette.success.darken70,
            '& .icon': {
                color: theme.palette.success.light
            }
        })
    },
    info: {
        defaultHeaderText: 'Info!',
        Icon: InfoOutlinedIcon,
        getContainerJss: (theme) => ({
            backgroundColor: theme.palette.info.lighten90,
            color: theme.palette.info.darken70,
            '& .icon': {
                color: theme.palette.info.light
            }
        })
    },
    warning: {
        defaultHeaderText: 'Warning!',
        Icon: WarningOutlinedIcon,
        getContainerJss: (theme) => ({
            backgroundColor: theme.palette.warning.lighten90,
            color: theme.palette.warning.darken70,
            '& .icon': {
                color: theme.palette.warning.light
            }
        })
    },
    error: {
        defaultHeaderText: 'Error!',
        Icon: ErrorOutlineOutlinedIcon,
        getContainerJss: (theme) => ({
            backgroundColor: theme.palette.error.lighten90,
            color: theme.palette.error.darken70,
            '& .icon': {
                color: theme.palette.error.light
            }
        })
    },
    default: {
        defaultHeaderText: 'Default',
        Icon: EmojiEmotionsOutlinedIcon,
        getContainerJss: (theme) => ({
            backgroundColor: theme.palette.grey[100],
            color: theme.palette.common.black,
            '& .icon': {
                color: theme.palette.common.black
            }
        })
    }
};

// This is rendered only once in root!
const rTToastStyles = makeStyles((theme) => ({
    wrapper: reduce(
        toastTypes,
        (acc, nextValue, nextKey) => {
            acc[`& .toast-container-${nextKey}`] = {
                borderRadius: '8px',
                padding: '16px',
                zIndex: 1500,
                position: 'fixed',
                opacity: '0.9 !important',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                ...nextValue.getContainerJss(theme)
            };
            return acc;
        },
        {}
    )
}));

const Toast = () => {
    const classes = rTToastStyles();
    return (
        <Box className={classes.wrapper}>
            <ToastContainer
                position="top-center"
                autoClose={4000}
                hideProgressBar
                closeOnClick={true}
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
        </Box>
    );
};

const RTToast = memo(Toast);

// This is what you use to display a toast!

// type: PropTypes.oneOf(['success', 'warning', 'error', 'info', 'default'])
// headerText: PropTypes.string
// bodyText: PropTypes.string
const displayToast = (type = 'default', headerText, bodyText) => {
    const typeName = toastTypes[type] ? type : 'default'; // fallback
    const toastType = toastTypes[typeName] || toastTypes.default;

    const CustomCloseButton = () => null;

    toast(
        <ToastContent
            headerText={headerText !== undefined ? headerText : toastType.defaultHeaderText}
            // No body falls back to NOTHING, not to the type's placeholder. Most
            // callers pass only a headline ("Travel file #123 closed
            // successfully") and were getting "This is a success toast" stapled
            // underneath it — developer scaffolding shown to users.
            bodyText={bodyText}
            Icon={toastType.Icon}
        />,
        {
            closeButton: <CustomCloseButton />,
            className: `toast-container-${typeName}`
        }
    );
};

export default RTToast;
export { toastTypes, ToastContent, displayToast };
